import React, { useMemo } from 'react';
import { useConvexAuth, useQuery } from 'convex/react';
import { api } from '../../convex/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { AccountInformation, CreditInquiry } from '../../types/api';
import { Loader2, Target, Flag, ChevronRight, Calendar, TrendingUp, ShoppingBag, CreditCard, Search, AlertCircle } from 'lucide-react';

interface RoadmapItem {
  itemNumber: number;
  type: 'milestone' | 'step';
  title: string;
  description: string;
  timeframe?: string;
  targetScore?: number;
  estimatedDate?: string;
  marketplaceButtonId?: string;
  disputeItemIds?: string[];
}

interface RoadmapSummary {
  goalScore: number;
  timeline: string;
  insight: string;
}

interface CreditRoadmapData {
  _id: string;
  user_id: string;
  analytics_id: string;
  summary: RoadmapSummary;
  roadmap: RoadmapItem[];
  created_at: number;
  updated_at: number;
}

type ResolvedItem =
  | { kind: 'account'; data: AccountInformation }
  | { kind: 'inquiry'; data: CreditInquiry };

interface CreditRoadmapProps {
  analyticsId: string | null;
  accounts?: AccountInformation[];
  inquiries?: CreditInquiry[];
}

export function CreditRoadmap({ analyticsId, accounts = [], inquiries = [] }: CreditRoadmapProps) {
  const itemLookup = useMemo(() => {
    const map = new Map<string, ResolvedItem>();
    for (const a of accounts) {
      map.set(a.id, { kind: 'account', data: a });
    }
    for (const i of inquiries) {
      map.set(i.id, { kind: 'inquiry', data: i });
    }
    return map;
  }, [accounts, inquiries]);
  const { isAuthenticated: isConvexAuth } = useConvexAuth();

  const roadmap = useQuery(
    api.credit_roadmaps.getByAnalyticsId,
    isConvexAuth && analyticsId ? { analytics_id: analyticsId } : 'skip'
  ) as CreditRoadmapData | null | undefined;

  if (!analyticsId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Target className="h-12 w-12 text-gray-300 mb-4" />
        <p className="text-sm text-muted-foreground">
          Select a credit report to view the roadmap.
        </p>
      </div>
    );
  }

  if (roadmap === undefined) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Loading roadmap...</p>
        </div>
      </div>
    );
  }

  if (roadmap === null) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Target className="h-12 w-12 text-gray-300 mb-4" />
        <p className="text-base font-medium text-gray-700 mb-1">No roadmap available</p>
        <p className="text-sm text-muted-foreground max-w-md">
          A credit roadmap will appear here after the report is fully analyzed.
        </p>
      </div>
    );
  }

  const { summary, roadmap: items } = roadmap;
  const milestoneCount = items.filter(i => i.type === 'milestone').length;
  const stepCount = items.filter(i => i.type === 'step').length;

  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <Card className="border-0 bg-gradient-to-br from-gray-50 to-white shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="h-14 w-14 rounded-2xl bg-gray-900 flex items-center justify-center">
                <span className="text-white font-bold text-lg">{summary.goalScore}</span>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Goal Score</p>
                <p className="text-sm font-semibold text-gray-900">{summary.timeline}</p>
              </div>
            </div>
            <div className="hidden sm:block h-10 w-px bg-gray-200" />
            <p className="text-sm text-gray-600 leading-relaxed flex-1">
              {summary.insight}
            </p>
            <div className="flex items-center gap-3 text-xs text-gray-500 flex-shrink-0">
              <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1">
                <Flag className="h-3 w-3" />
                {milestoneCount} milestone{milestoneCount !== 1 ? 's' : ''}
              </span>
              <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1">
                <ChevronRight className="h-3 w-3" />
                {stepCount} step{stepCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <div className="relative">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isMilestone = item.type === 'milestone';

          return (
            <div key={item.itemNumber} className="relative flex gap-4 pb-1">
              {/* Vertical line connector */}
              {!isLast && (
                <div
                  className="absolute left-[15px] top-[32px] w-px bg-gray-200"
                  style={{ height: 'calc(100% - 16px)' }}
                />
              )}

              {/* Node indicator */}
              <div className="relative z-10 flex-shrink-0 mt-1">
                {isMilestone ? (
                  <div className="h-[30px] w-[30px] rounded-full bg-gray-900 flex items-center justify-center ring-4 ring-white">
                    <Flag className="h-3.5 w-3.5 text-white" />
                  </div>
                ) : (
                  <div className="h-[30px] w-[30px] rounded-full border-2 border-gray-300 bg-white flex items-center justify-center ring-4 ring-white">
                    <span className="text-xs font-semibold text-gray-500">{item.itemNumber}</span>
                  </div>
                )}
              </div>

              {/* Content card */}
              <div className={`flex-1 mb-4 rounded-xl border p-4 ${
                isMilestone
                  ? 'bg-gray-50 border-gray-200'
                  : 'bg-white border-gray-100'
              }`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${
                        isMilestone
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {item.type}
                      </span>
                      {isMilestone && item.targetScore != null && (
                        <span className="inline-flex items-center gap-1 text-sm font-bold text-gray-900">
                          <TrendingUp className="h-3.5 w-3.5" />
                          {item.targetScore}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mt-1.5">{item.title}</p>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{item.description}</p>
                  </div>

                  {/* Timeframe / Estimated Date badge */}
                  {(item.timeframe || item.estimatedDate) && (
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1">
                        <Calendar className="h-3 w-3" />
                        {item.timeframe || item.estimatedDate}
                      </span>
                    </div>
                  )}
                </div>

                {/* Marketplace CTA */}
                {item.marketplaceButtonId && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs font-medium gap-1.5"
                    >
                      <ShoppingBag className="h-3.5 w-3.5" />
                      View Products
                    </Button>
                  </div>
                )}

                {/* Inline dispute item details */}
                {item.disputeItemIds && item.disputeItemIds.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    {item.disputeItemIds.map((id) => {
                      const resolved = itemLookup.get(id);
                      if (!resolved) {
                        return (
                          <div key={id} className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
                            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="font-mono">{id}</span>
                          </div>
                        );
                      }
                      if (resolved.kind === 'account') {
                        const a = resolved.data;
                        return (
                          <div key={id} className="flex items-start gap-2.5 text-xs bg-gray-50 rounded-lg px-3 py-2.5">
                            <CreditCard className="h-3.5 w-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-gray-900">{a.creditorDisplayName || a.creditorName}</span>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-500 font-mono">{a.accountNumber}</span>
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-gray-500 flex-wrap">
                                <span>{a.accountType}</span>
                                <span className="text-gray-300">|</span>
                                <span>Balance: {a.currentBalance}</span>
                                <span className="text-gray-300">|</span>
                                <span className={a.negativeAccount ? 'text-red-600 font-medium' : ''}>{a.accountStatus}</span>
                                <span className="text-gray-300">|</span>
                                <span>{a.bureau}</span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      const inq = resolved.data;
                      return (
                        <div key={id} className="flex items-start gap-2.5 text-xs bg-gray-50 rounded-lg px-3 py-2.5">
                          <Search className="h-3.5 w-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-semibold text-gray-900">{inq.company}</span>
                            <div className="flex items-center gap-3 mt-1 text-gray-500">
                              <span>{inq.date}</span>
                              <span className="text-gray-300">|</span>
                              <span>{inq.bureau}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="text-center pt-2 pb-4">
        <p className="text-xs text-gray-400">
          Roadmap generated {new Date(roadmap.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>
    </div>
  );
}
